/**
 * The gate, 2026 edition. Readers "sign up" and the wall respects the
 * signup the way the archive respects deadlines. Every presenter here
 * is fully client-side; there is no backend to hold accountable.
 */

export type ReaderState = "anonymous" | "signed-up";

const READER_KEY = "wire.reader.v1";

export function readerState(): ReaderState {
  try {
    return localStorage.getItem(READER_KEY) ? "signed-up" : "anonymous";
  } catch {
    return "anonymous";
  }
}

export function markSignedUp(name: string, email: string): void {
  try {
    localStorage.setItem(
      READER_KEY,
      JSON.stringify({
        name,
        email,
        at: new Date().toISOString(),
        universes: 1,
      }),
    );
  } catch {
    /* private mode readers stay anonymous; the wall forgives them */
  }
}

/** The one honest line in the product. Signed up. That's all it does. */
export const SIGNUP_OK = "You're signed up.";
export const SIGNUP_FINEPRINT = "That's all a signup does.";
export const SIGNUP_FORM_FINEPRINT = "No account is created. The wall remains a wall.";

export const WALL_PENDING = "Subscription pending editorial review. Reviews take 5–7 universes.";
export const SEALED_NOTE = "This dispatch is sealed. Authorized editors only.";
export const SEALED_FINEPRINT = "Sealed by the desk. The desk is not accepting appeals.";

/* ------------------------------------------------------------------ */
/* Modal plumbing (shared shell for all three walls)                   */
/* ------------------------------------------------------------------ */

let shell: HTMLElement | null = null;

function modalShell(): { root: HTMLElement; body: HTMLElement } {
  if (shell && document.body.contains(shell)) {
    const body = shell.querySelector<HTMLElement>(".wire-modal__body");
    if (body) return { root: shell, body };
  }
  shell = document.createElement("div");
  shell.className = "wire-modal";
  shell.innerHTML = `
    <div class="wire-modal__scrim" data-close="1"></div>
    <div class="wire-modal__paper" role="dialog" aria-modal="true">
      <button class="wire-modal__x" type="button" aria-label="Close" data-close="1">×</button>
      <div class="wire-modal__body"></div>
    </div>`;
  document.body.appendChild(shell);
  shell.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t.dataset.close) closeModal();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeModal();
  });
  const body = shell.querySelector<HTMLElement>(".wire-modal__body");
  return { root: shell, body: body as HTMLElement };
}

export function closeModal(): void {
  if (shell) {
    shell.remove();
    shell = null;
  }
}

function open(bodyHtml: string): HTMLElement {
  const { root, body } = modalShell();
  body.innerHTML = bodyHtml;
  root.classList.add("is-open");
  const sketch = (window as unknown as { WireSketch?: { wireCard(el: HTMLElement): void } }).WireSketch;
  const paper = root.querySelector<HTMLElement>(".wire-modal__paper");
  if (sketch && paper) sketch.wireCard(paper);
  const x = root.querySelector<HTMLElement>(".wire-modal__x");
  if (sketch && x) sketch.wireButton(x);
  return body;
}

/* ------------------------------------------------------------------ */
/* The signup wall                                                     */
/* ------------------------------------------------------------------ */

export function presentSignup(): void {
  const body = open(`
    <p class="wire-kicker">Free reader registration</p>
    <h3 class="wire-modal__title">Sign up to keep reading</h3>
    <form class="wire-signup">
      <label class="wire-field"><span>Name</span><input type="text" name="name" autocomplete="name" /></label>
      <label class="wire-field"><span>Email, any universe</span><input type="email" name="email" autocomplete="email" /></label>
      <label class="wire-field"><span>Password</span><input type="password" name="password" autocomplete="new-password" /></label>
      <button class="wire-cta" type="submit">Sign up</button>
      <p class="wire-signup__fine">${SIGNUP_FORM_FINEPRINT}</p>
    </form>`);
  const sketch = (window as unknown as { WireSketch?: Record<string, (el: HTMLElement) => void> }).WireSketch;
  for (const field of Array.from(body.querySelectorAll<HTMLElement>(".wire-field"))) {
    sketch?.wireInput(field);
  }
  const cta = body.querySelector<HTMLElement>(".wire-cta");
  if (sketch && cta) (sketch.wireButton as unknown as (el: HTMLElement) => unknown)(cta);

  const form = body.querySelector<HTMLFormElement>(".wire-signup");
  form?.addEventListener("submit", (e) => {
    e.preventDefault();
    const name = (form.elements.namedItem("name") as HTMLInputElement)?.value.trim() ?? "";
    const email = (form.elements.namedItem("email") as HTMLInputElement)?.value.trim() ?? "";
    markSignedUp(name || "reader", email || "reader@everywhere");
    body.innerHTML = `
      <p class="wire-kicker">Free reader registration</p>
      <h3 class="wire-modal__title">${SIGNUP_OK}</h3>
      <p class="wire-signup__fine">${SIGNUP_FINEPRINT}</p>
      <button class="wire-cta" type="button" data-close="1">Back to the front page</button>`;
    const back = body.querySelector<HTMLElement>('[data-close="1"]');
    if (sketch && back) (sketch.wireButton as unknown as (el: HTMLElement) => unknown)(back);
  });
}

/* ------------------------------------------------------------------ */
/* The paywall (for readers who did sign up — same wall, new words)     */
/* ------------------------------------------------------------------ */

export function presentWall(): void {
  const state = readerState();
  const body = open(`
    <p class="wire-kicker">${state === "signed-up" ? "Free reader" : "Registration required"}</p>
    <h3 class="wire-modal__title">${state === "signed-up" ? "Almost reading" : "Keep reading"}</h3>
    <p class="wire-modal__copy">${WALL_PENDING}</p>
    ${
      state === "anonymous"
        ? `<button class="wire-cta" type="button" data-signup="1">Sign up</button>`
        : `<button class="wire-cta" type="button" data-close="1">Return to the front page</button>`
    }`);
  const sketch = (window as unknown as { WireSketch?: Record<string, (el: HTMLElement) => unknown> }).WireSketch;
  const cta = body.querySelector<HTMLElement>(".wire-cta");
  if (sketch && cta) sketch.wireButton(cta);
  cta?.addEventListener("click", () => {
    if (cta.dataset.signup) presentSignup();
  });
}

/* ------------------------------------------------------------------ */
/* The sealed dispatch (premium). No signup option. On purpose.        */
/* ------------------------------------------------------------------ */

export function presentSealed(): void {
  const body = open(`
    <p class="wire-kicker">Premium · Edition 41.2</p>
    <h3 class="wire-modal__title">Sealed dispatch</h3>
    <p class="wire-modal__copy">${SEALED_NOTE}</p>
    <p class="wire-signup__fine">${SEALED_FINEPRINT}</p>
    <button class="wire-cta" type="button" data-close="1">Understood</button>`);
  const sketch = (window as unknown as { WireSketch?: Record<string, (el: HTMLElement) => unknown> }).WireSketch;
  const cta = body.querySelector<HTMLElement>(".wire-cta");
  if (sketch && cta) sketch.wireButton(cta);
}
